import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Badge,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Chip,
  Tooltip,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  NotificationsActive as BellActiveIcon,
  Notifications as BellIcon,
  Close as CloseIcon,
  MonitorHeart as VitalsIcon,
  EditNote as NoteIcon,
  CheckCircle as CheckIcon,
  ArrowBack as ArrowBackIcon,
  WarningAmberRounded as WarningIcon,
} from '@mui/icons-material';
import { socket } from '../services/socket';
import {
  complianceFollowups,
  complianceFollowupDismiss,
} from '../BaseURL';

const PRIMARY        = '#41a386';
const PRIMARY_DARK   = '#2f8068';
const WARN           = '#b45309';
const WARN_BG        = 'rgba(234,179,8,0.10)';
const WARN_BORDER    = 'rgba(234,179,8,0.35)';
const DANGER         = '#b91c1c';
const DANGER_BG      = 'rgba(220,38,38,0.10)';
const BORDER         = '#e5e9ef';
const MUTED          = '#64748b';
const STRONG         = '#0f172a';

/**
 * Bell-icon badge that nurses see at the top of their InPatient panels.
 * Real-time count of pending compliance follow-ups raised by doctors when
 * a discharge is blocked. Click → list dialog → click a row → detail
 * dialog with per-day breakdown + "Record Vitals" / "Add Note" jump links.
 *
 * Props:
 *   wardIds            comma-separated string of ward ids to scope to
 *   onAddVitals(row)   nurse selected "Record Vitals" for a patient
 *   onAddNote(row)     nurse selected "Add Nursing Note" for a patient
 *   currentUser        username string, sent to backend on dismiss
 */
const ComplianceFollowupBell = ({ wardIds = '', onAddVitals, onAddNote, currentUser }) => {
  const [list, setList]               = useState([]);
  const [minVitalsPerDay, setMinVitalsPerDay] = useState(3);
  const [minNotesPerDay,  setMinNotesPerDay]  = useState(1);
  const [loading, setLoading]         = useState(false);
  const [listOpen, setListOpen]       = useState(false);
  const [detailOpen, setDetailOpen]   = useState(false);
  const [selected, setSelected]       = useState(null);
  const [pulse, setPulse]             = useState(false);

  const wardIdsRef = useRef(wardIds);
  useEffect(() => { wardIdsRef.current = wardIds; }, [wardIds]);

  // In-flight guard so several socket events firing in quick succession
  // don't queue parallel /compliance/followups requests against a slow DB.
  const inFlightRef = useRef(false);

  const fetchList = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    try {
      // Hard 12s timeout: the bell never gets stuck in a permanent spinner
      // even if the DB / network momentarily hangs. On error we treat the
      // list as empty so the user sees "no outstanding" instead of spinning.
      const { data } = await axios.get(complianceFollowups, {
        params: wardIdsRef.current ? { wardIds: wardIdsRef.current } : {},
        timeout: 12000,
      });
      if (data?.success) {
        setList(data.results || []);
        setMinVitalsPerDay(data.minVitalsPerDay ?? data.minPerDay ?? 3);
        setMinNotesPerDay( data.minNotesPerDay  ?? 1);
      } else {
        setList([]);
      }
    } catch (err) {
      console.error('Compliance follow-ups load failed:', err.message || err);
      setList([]);
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  }, []);

  // Initial load + real-time socket subscription
  useEffect(() => {
    fetchList();

    if (!socket.connected) {
      try { socket.connect(); } catch (_) {}
    }

    const onFollowup = (payload) => {
      // A doctor just raised a new follow-up. Refresh and pulse the bell.
      fetchList();
      setPulse(true);
      setTimeout(() => setPulse(false), 1800);
    };
    const onResolved = () => {
      fetchList();
    };

    socket.on('compliance:followup', onFollowup);
    socket.on('compliance:followup-resolved', onResolved);
    return () => {
      socket.off('compliance:followup', onFollowup);
      socket.off('compliance:followup-resolved', onResolved);
    };
  }, [fetchList]);

  // Refetch whenever the ward scope changes
  useEffect(() => { fetchList(); }, [wardIds, fetchList]);

  const openDetail = (row) => {
    setSelected(row);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelected(null);
  };

  const handleDismiss = async (id) => {
    try {
      await axios.post(complianceFollowupDismiss(id), { dismissedBy: currentUser || null });
      // Optimistic update; socket event will also re-fetch
      setList(prev => prev.filter(r => r.id !== id));
      if (selected?.id === id) closeDetail();
    } catch (err) {
      console.error('Dismiss follow-up failed:', err);
    }
  };

  const triggerAction = (row, action) => {
    closeDetail();
    setListOpen(false);
    if (action === 'vitals' && onAddVitals) onAddVitals(row);
    if (action === 'notes'  && onAddNote)   onAddNote(row);
  };

  const count = list.length;

  return (
    <>
      <Tooltip title={count > 0 ? `${count} compliance follow-up${count === 1 ? '' : 's'}` : 'No pending compliance follow-ups'} arrow>
        <span>
          <IconButton
            size="small"
            onClick={() => setListOpen(true)}
            sx={{
              border: `1px solid ${count > 0 ? WARN_BORDER : BORDER}`,
              bgcolor: count > 0 ? WARN_BG : '#fff',
              color: count > 0 ? WARN : MUTED,
              animation: pulse ? 'compliance-pulse 0.9s ease-in-out 2' : 'none',
              '@keyframes compliance-pulse': {
                '0%, 100%': { transform: 'scale(1)' },
                '50%':      { transform: 'scale(1.15)' },
              },
              '&:hover': {
                bgcolor: count > 0 ? 'rgba(234,179,8,0.18)' : '#f1f5f9',
              },
            }}
          >
            <Badge
              badgeContent={count}
              color="error"
              max={99}
              overlap="circular"
            >
              {count > 0 ? <BellActiveIcon sx={{ fontSize: 18 }} /> : <BellIcon sx={{ fontSize: 18 }} />}
            </Badge>
          </IconButton>
        </span>
      </Tooltip>

      {/* List dialog: every pending follow-up */}
      <Dialog open={listOpen} onClose={() => setListOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1.25,
          bgcolor: WARN_BG, color: WARN,
          borderBottom: `1px solid ${WARN_BORDER}`,
          px: 2, py: 1.25,
        }}>
          <WarningIcon sx={{ fontSize: 20 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.8px' }}>
              COMPLIANCE FOLLOW-UPS
            </Typography>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.2 }}>
              {count} patient{count === 1 ? '' : 's'} needing attention
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setListOpen(false)} sx={{ color: WARN }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
          {loading && count === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, gap: 1.25 }}>
              <CircularProgress size={18} sx={{ color: PRIMARY }} />
              <Typography sx={{ fontSize: '0.85rem', color: MUTED }}>Loading…</Typography>
            </Box>
          ) : count === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckIcon sx={{ fontSize: 32, color: PRIMARY, mb: 0.75 }} />
              <Typography sx={{ fontSize: '0.88rem', color: PRIMARY_DARK, fontWeight: 600 }}>
                No outstanding follow-ups.
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: MUTED, mt: 0.5 }}>
                Doctors haven't raised any compliance alerts.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {list.map(row => (
                <Box
                  key={row.id}
                  onClick={() => openDetail(row)}
                  sx={{
                    p: 1.25,
                    borderRadius: 1.25,
                    border: `1px solid ${BORDER}`,
                    bgcolor: '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: PRIMARY,
                      boxShadow: '0 2px 8px rgba(15,23,42,0.06)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG }}>
                        {row.patientName || row.patientId}
                        <Typography component="span" sx={{ ml: 1, fontSize: '0.72rem', color: MUTED, fontWeight: 500 }}>
                          #{row.patientId}
                        </Typography>
                      </Typography>
                      <Stack direction="row" spacing={1.5} sx={{ mt: 0.25, flexWrap: 'wrap' }}>
                        {row.wardName && (
                          <Typography sx={{ fontSize: '0.72rem', color: MUTED }}>
                            Ward: <Typography component="span" sx={{ fontSize: '0.72rem', color: STRONG, fontWeight: 600 }}>
                              {row.wardName}{row.bedNumber ? ` · Bed ${row.bedNumber}` : ''}
                            </Typography>
                          </Typography>
                        )}
                        <Typography sx={{ fontSize: '0.72rem', color: MUTED }}>
                          Admitted: <Typography component="span" sx={{ fontSize: '0.72rem', color: STRONG, fontWeight: 600 }}>{row.admissionDate}</Typography>
                        </Typography>
                        {row.requestedBy && (
                          <Typography sx={{ fontSize: '0.72rem', color: MUTED }}>
                            From Dr. <Typography component="span" sx={{ fontSize: '0.72rem', color: STRONG, fontWeight: 600 }}>{row.requestedBy}</Typography>
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                    <Chip
                      size="small"
                      label={`${row.totalIncomplete} day${row.totalIncomplete === 1 ? '' : 's'}`}
                      sx={{
                        height: 22, fontSize: '0.72rem', fontWeight: 700,
                        bgcolor: DANGER_BG, color: DANGER, border: `1px solid rgba(220,38,38,0.3)`,
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.25, borderTop: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
          <Button
            onClick={() => setListOpen(false)}
            sx={{ textTransform: 'none', color: MUTED, fontSize: '0.82rem' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail dialog: per-day breakdown for one selected patient */}
      <Dialog open={detailOpen} onClose={closeDetail} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1.25,
          bgcolor: WARN_BG, color: WARN,
          borderBottom: `1px solid ${WARN_BORDER}`,
          px: 2, py: 1.25,
        }}>
          <IconButton size="small" onClick={() => { setDetailOpen(false); setSelected(null); }} sx={{ color: WARN }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.8px' }}>
              COMPLIANCE GAPS · {selected?.patientId}
            </Typography>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.2 }}>
              {selected?.patientName || selected?.patientId}
            </Typography>
          </Box>
          <IconButton size="small" onClick={closeDetail} sx={{ color: WARN }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 1.75, bgcolor: '#f8fafc' }}>
          {selected && (
            <>
              <Typography sx={{ fontSize: '0.8rem', color: MUTED, mb: 1.25 }}>
                Every admission day needs at least <strong>{minVitalsPerDay} vitals</strong> and <strong>{minNotesPerDay} nursing note{minNotesPerDay === 1 ? '' : 's'}</strong>.
                Tap the buttons below to jump into the right tab for this patient.
              </Typography>

              <Stack spacing={0.75}>
                {(selected.incompleteDays || []).map(day => {
                  const missing = [];
                  if (day.missingVitals > 0) missing.push(`${day.missingVitals} vital${day.missingVitals === 1 ? '' : 's'}`);
                  if (day.missingNotes  > 0) missing.push(`${day.missingNotes} note${day.missingNotes === 1 ? '' : 's'}`);
                  return (
                    <Box
                      key={day.date}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                        px: 1.25, py: 0.85,
                        borderRadius: 1,
                        bgcolor: '#fff',
                        border: `1px solid ${BORDER}`,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: STRONG, minWidth: 92 }}>
                        {day.date}
                      </Typography>
                      <Chip
                        size="small"
                        icon={<VitalsIcon sx={{ fontSize: 12 }} />}
                        label={`Vitals ${day.vitals}/${day.minVitals ?? minVitalsPerDay}`}
                        sx={{
                          height: 22, fontSize: '0.72rem', fontWeight: 600,
                          bgcolor: day.missingVitals === 0 ? 'rgba(65,163,134,0.12)' : DANGER_BG,
                          color:   day.missingVitals === 0 ? PRIMARY_DARK : DANGER,
                          border:  `1px solid ${day.missingVitals === 0 ? 'rgba(65,163,134,0.3)' : 'rgba(220,38,38,0.3)'}`,
                          '& .MuiChip-icon': { color: 'inherit', ml: '4px' },
                        }}
                      />
                      <Chip
                        size="small"
                        icon={<NoteIcon sx={{ fontSize: 12 }} />}
                        label={`Notes ${day.notes}/${day.minNotes ?? minNotesPerDay}`}
                        sx={{
                          height: 22, fontSize: '0.72rem', fontWeight: 600,
                          bgcolor: day.missingNotes === 0 ? 'rgba(65,163,134,0.12)' : DANGER_BG,
                          color:   day.missingNotes === 0 ? PRIMARY_DARK : DANGER,
                          border:  `1px solid ${day.missingNotes === 0 ? 'rgba(65,163,134,0.3)' : 'rgba(220,38,38,0.3)'}`,
                          '& .MuiChip-icon': { color: 'inherit', ml: '4px' },
                        }}
                      />
                      {missing.length > 0 && (
                        <Typography sx={{ fontSize: '0.72rem', color: MUTED, fontStyle: 'italic' }}>
                          missing: {missing.join(', ')}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>

              <Divider sx={{ my: 1.5 }} />

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  size="small"
                  startIcon={<VitalsIcon sx={{ fontSize: 16 }} />}
                  onClick={() => triggerAction(selected, 'vitals')}
                  sx={{
                    textTransform: 'none', fontSize: '0.78rem', fontWeight: 600,
                    px: 1.5, borderRadius: 1,
                    border: `1px solid ${PRIMARY}`, color: PRIMARY,
                    '&:hover': { bgcolor: 'rgba(65,163,134,0.08)' },
                  }}
                >
                  Record Vitals
                </Button>
                <Button
                  size="small"
                  startIcon={<NoteIcon sx={{ fontSize: 16 }} />}
                  onClick={() => triggerAction(selected, 'notes')}
                  sx={{
                    textTransform: 'none', fontSize: '0.78rem', fontWeight: 600,
                    px: 1.5, borderRadius: 1,
                    border: `1px solid ${PRIMARY}`, color: PRIMARY,
                    '&:hover': { bgcolor: 'rgba(65,163,134,0.08)' },
                  }}
                >
                  Add Nursing Note
                </Button>
              </Stack>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.25, borderTop: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
          <Button
            onClick={() => selected && handleDismiss(selected.id)}
            sx={{ textTransform: 'none', color: MUTED, fontSize: '0.82rem' }}
          >
            Dismiss
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={closeDetail}
            sx={{ textTransform: 'none', fontSize: '0.82rem', fontWeight: 600,
                  bgcolor: PRIMARY, color: '#fff',
                  '&:hover': { bgcolor: PRIMARY_DARK } }}
            variant="contained"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ComplianceFollowupBell;
